import { Fragment, ReactNode } from "react";

function safeUrl(url: string): string | null {
  try {
    const parsed = new URL(url);
    return parsed.protocol === "https:" ? parsed.toString() : null;
  } catch {
    return null;
  }
}

function renderInline(text: string): ReactNode[] {
  const pattern = /\[([^\]]+)\]\((https:\/\/[^)\s]+)\)/g;
  const result: ReactNode[] = [];
  let lastIndex = 0;
  let match = pattern.exec(text);

  while (match) {
    if (match.index > lastIndex) {
      result.push(text.slice(lastIndex, match.index));
    }

    const url = safeUrl(match[2]);
    result.push(
      url ? (
        <a key={`${match.index}-${url}`} href={url} target="_blank" rel="noreferrer">
          {match[1]}
        </a>
      ) : (
        match[1]
      )
    );

    lastIndex = pattern.lastIndex;
    match = pattern.exec(text);
  }

  if (lastIndex < text.length) {
    result.push(text.slice(lastIndex));
  }

  return result;
}

export function MarkdownBody({ markdown }: { markdown: string }) {
  const lines = markdown.replace(/\r/g, "").split("\n");
  const blocks: ReactNode[] = [];
  let paragraph: string[] = [];
  let list: string[] = [];

  function flushParagraph() {
    if (paragraph.length > 0) {
      const text = paragraph.join(" ");
      blocks.push(<p key={`p-${blocks.length}`}>{renderInline(text)}</p>);
      paragraph = [];
    }
  }

  function flushList() {
    if (list.length > 0) {
      blocks.push(
        <ul key={`ul-${blocks.length}`}>
          {list.map((item, index) => (
            <li key={`${index}-${item}`}>{renderInline(item)}</li>
          ))}
        </ul>
      );
      list = [];
    }
  }

  lines.forEach((line) => {
    const trimmed = line.trim();

    if (!trimmed) {
      flushParagraph();
      flushList();
      return;
    }

    if (trimmed.startsWith("# ")) {
      flushParagraph();
      flushList();
      // The article page already supplies the document's single visible h1.
      return;
    }

    if (trimmed.startsWith("## ")) {
      flushParagraph();
      flushList();
      blocks.push(
        <h2 key={`h2-${blocks.length}`}>{renderInline(trimmed.slice(3))}</h2>
      );
      return;
    }

    if (trimmed.startsWith("### ")) {
      flushParagraph();
      flushList();
      blocks.push(
        <h3 key={`h3-${blocks.length}`}>{renderInline(trimmed.slice(4))}</h3>
      );
      return;
    }

    if (trimmed.startsWith("- ")) {
      flushParagraph();
      list.push(trimmed.slice(2));
      return;
    }

    flushList();
    paragraph.push(trimmed);
  });

  flushParagraph();
  flushList();

  return (
    <div className="markdown-body">
      {blocks.map((block, index) => (
        <Fragment key={index}>{block}</Fragment>
      ))}
    </div>
  );
}
